// -*- coding: utf-8 -*-
/**
 * 动态菜单路由生成器
 * 根据后端返回的菜单数据动态生成 Vue Router 路由表
 */

import Layout from '@/layouts/MainLayout.vue'

// 动态导入的视图组件映射表
const constantRouterComponents = {
  // 基础布局
  Layout: Layout,

  // 系统管理模块
  AdminUser: () => import('@/views/Admin/User.vue'),
  AdminRole: () => import('@/views/Admin/Role.vue'),
  AdminPermission: () => import('@/views/Admin/Permission.vue'),
  AdminMenu: () => import('@/views/Admin/Menu.vue'),

  // CRM
  CrmPersonnel: () => import('@/views/CRM/Personnel.vue'),
  CrmQualification: () => import('@/views/CRM/Qualification.vue'),
  CrmCustomer: () => import('@/views/CRM/Customer.vue'),
  CrmAlert: () => import('@/views/CRM/Alert.vue'),
  CrmSentiment: () => import('@/views/CRM/Sentiment.vue'),

  // 审计模块
  AuditLogs: () => import('@/views/Audit/AuditLogs.vue'),

  // 错误页面
  NotFound: () => import('@/views/NotFound.vue'),
  Dashboard: () => import('@/views/Dashboard.vue'),
}

/**
 * 404 页面路由（必须放在最后）
 */
const notFoundRouter = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: constantRouterComponents.NotFound,
  meta: { requiresAuth: false }
}

/**
 * 动态生成路由表
 * @param {Array} menuData - 后端返回的菜单数据
 * @returns {Promise<Array>} - 生成的路由表
 */
export const generatorDynamicRouter = (menuData) => {
  return new Promise((resolve, reject) => {
    try {
      if (!menuData || !Array.isArray(menuData)) {
        reject(new Error('菜单数据格式错误'))
        return
      }

      // 生成路由树
      const routers = generator(menuData)

      // 添加 404 路由（必须在最后）
      routers.push(notFoundRouter)

      resolve(routers)
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 递归生成路由树
 * 将菜单数据格式化为 Vue Router 可识别的路由配置
 *
 * @param {Array} routerMap - 菜单数据数组
 * @param {Object} parent - 父菜单对象
 * @returns {Array} - 格式化后的路由数组
 */
export const generator = (routerMap, parent) => {
  return routerMap.map((item) => {
    const {
      name,
      title,
      path,
      icon,
      is_active,
      sort_order,
      component,
      children = []
    } = item || {}

    // 处理路径：如果是绝对路径且是顶级路由，移除前导斜杠
    let routePath = path || `${(parent && parent.path) || ''}/${item.name || item.key}`;
    
    // 如果没有父路由（顶级路由）且路径以 / 开头，移除前导斜杠
    if (!parent && routePath.startsWith('/')) {
      routePath = routePath.substring(1);
    }
    
    // 修复路径中可能出现的双斜杠
    if (!routePath.startsWith('http')) {
      routePath = routePath.replace(/\/+/g, '/');
    }
    
    // 检查是否有子菜单
    const hasChildren = children && children.length > 0;

    // 构建当前路由对象
    const currentRouter = {
      // 路由路径
      path: routePath,
      // 路由名称
      name: name || item.key || '',
      // 动态组件：根据菜单的 component 字段查找对应的组件
      component: getComponent(component, item.name, hasChildren),
      // 路由元数据
      meta: {
        title: title || name || '',
        icon: icon || '',
        sortOrder: sort_order || 0,
        requiresAuth: true
      }
    }

    // 如果菜单被禁用，设置隐藏标记
    if (is_active === false) {
      currentRouter.hidden = true
    }

    // 如果有子菜单，递归处理
    if (hasChildren) {
      currentRouter.children = generator(children, currentRouter)
    }

    return currentRouter
  })
}

/**
 * 获取对应的组件
 * 根据菜单的 component 字段或 name 字段查找组件映射
 *
 * @param {String} componentName - 组件名称
 * @param {String} menuName - 菜单名称（备用）
 * @param {Boolean} hasChildren - 是否有子菜单
 * @returns {Function|Object} - 组件对象或方法
 */
function getComponent(componentName, menuName, hasChildren = false) {
  // 如果有子菜单，不需要组件（父菜单只是分组）
  if (hasChildren) {
    return null;
  }
  
  if (!componentName && !menuName) {
    // 如果没有指定组件，返回 null
    console.warn(`[routerGenerator] 菜单没有指定组件`);
    return null;
  }

  // 优先使用指定的组件名
  const name = componentName || menuName
  const component = constantRouterComponents[name]

  if (component) {
    return component
  }

  // 如果没有找到对应组件，记录警告
  console.warn(`[routerGenerator] 未找到组件: ${name}`);
  return null;
}

/**
 * 获取菜单树中指定路径的组件
 *
 * @param {Array} menuData - 菜单数据
 * @param {String} targetPath - 目标路径
 * @returns {Object|null} - 找到的菜单项或 null
 */
export const findMenuByPath = (menuData, targetPath) => {
  if (!menuData || !Array.isArray(menuData)) {
    return null
  }

  for (const item of menuData) {
    if (item.path === targetPath) {
      return item
    }

    if (item.children && item.children.length > 0) {
      const found = findMenuByPath(item.children, targetPath)
      if (found) {
        return found
      }
    }
  }

  return null
}

/**
 * 展平菜单树为一维数组（用于搜索等场景）
 *
 * @param {Array} menuData - 菜单数据
 * @returns {Array} - 展平后的菜单数组
 */
export const flattenMenu = (menuData) => {
  const result = []

  function traverse(menus) {
    if (!menus || !Array.isArray(menus)) {
      return
    }

    menus.forEach((item) => {
      result.push(item)
      if (item.children && item.children.length > 0) {
        traverse(item.children)
      }
    })
  }

  traverse(menuData)
  return result
}

/**
 * 按 sort_order 排序菜单
 *
 * @param {Array} menuData - 菜单数据
 * @returns {Array} - 排序后的菜单数据
 */
export const sortMenuByOrder = (menuData) => {
  if (!menuData || !Array.isArray(menuData)) {
    return []
  }

  return menuData
    .map((item) => ({
      ...item,
      children: sortMenuByOrder(item.children || [])
    }))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
}

export default {
  generatorDynamicRouter,
  generator,
  findMenuByPath,
  flattenMenu,
  sortMenuByOrder
}
